import Image from "next/image";
import { useRouter } from "next/router";

import { Button, Card, Disclosure } from "@chef/components";
import { BRAND_PATHS } from "@chef/constants";

import { useCalendarData } from "@chef/state-management/hooks";
import { Breakfast, ChevronRight } from "@chef/icons/small";
import { isEqualStrings } from "@chef/utils/equal";

import { intl } from "./StandaloneGoods.Intl";

interface StandaloneGoodsProps {
  week: number;
  year: number;
}

export const StandaloneGoodsDisclosure = ({
  week,
  year,
}: StandaloneGoodsProps) => {
  const router = useRouter();

  const {
    basket: { groceryBasket },
    productsByCategories,
  } = useCalendarData({
    week,
    year,
  });

  // const navigateWithDeviationCheck = useNavigateWithDeviationCheck({
  //   week,
  //   year,
  //   calendarBasketProducts: groceryBasket,
  // });

  const productsByCategoriesVariations = productsByCategories
    ?.flatMap((c) => c.products)
    ?.flatMap((p) => p.variations);

  const list = groceryBasket.map((p) => ({
    variationId: p.variationId,
    quantity: p.quantity,
    variation: productsByCategoriesVariations?.find((pbcv) =>
      isEqualStrings(pbcv.variationId, p.variationId),
    ),
  }));

  const handleGoToStandaloneGrocery = async () => {
    //was decided by Martin to comment this out for now
    // const shouldAbortNavigation = await navigateWithDeviationCheck(
    //   BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year }),
    // );
    // if (!shouldAbortNavigation) {
    //   router.push(BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year }));
    // }
    router.push(BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year }));
  };

  if (!list || list.length === 0) {
    return (
      <div className="col-span-full lg:col-span-4 ">
        <Card className="!p-0">
          <Disclosure
            id="standalone-goods-disclosure-empty"
            className="py-4 pr-4"
            Title={() => (
              <div className="flex flex-row justify-between text-base grow">
                <strong>{intl.STANDALONE_GOODS}</strong>
                <span className="mr-2">0 {intl.PIECES}</span>
              </div>
            )}
            Icon={() => <Breakfast className="w-5 h-5 ml-4 mr-3" />}
          >
            <div className="relative w-full px-4 pb-4 h-36">
              <Image
                alt={intl.SPECIAL_GOODS}
                src="https://ggfrontendassets.azureedge.net/publicassets/retnemt/specialvarer.png"
                layout="fill"
                className="object-contain"
              />
            </div>

            <div className="p-6">
              <h3 className="mb-1">
                <strong>{intl.BUY_SPECIAL_GOODS}</strong>
              </h3>

              <p className="mb-6">{intl.SPECIAL_GOODS_DESCRIPTION}</p>

              <Button primary onClick={handleGoToStandaloneGrocery}>
                {intl.LINK_TEXT_CHANGE_ADDONS} <ChevronRight />
              </Button>
            </div>
          </Disclosure>
        </Card>
      </div>
    );
  }

  const standaloneGoodsCount = list.reduce(
    (acc, { quantity }) => acc + quantity,
    0,
  );

  return (
    <div className="col-span-full lg:col-span-4">
      <Card className="!p-0">
        <Disclosure
          id="standalone-goods-disclosure"
          className="py-4 pr-4"
          Title={() => (
            <div className="flex flex-row justify-between text-base grow">
              <strong>{intl.STANDALONE_GOODS}</strong>
              <span className="mr-2">
                {standaloneGoodsCount} {intl.PIECES}
              </span>
            </div>
          )}
          Icon={() => <Breakfast className="w-5 h-5 ml-4 mr-3" />}
        >
          <ul
            id="standalone-goods-list"
            className="relative flex flex-col px-4 gap-y-1"
          >
            {list.map(({ variation, quantity }) => {
              if (!variation) {
                return null;
              }

              const { name, variationId } = variation;

              return (
                <li
                  key={variationId}
                  className="flex items-center justify-between"
                >
                  <span>{name}</span>
                  <span>
                    {quantity} {intl.PIECES}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="pb-4 mt-6 text-center">
            <button
              className="underline text-primary"
              onClick={handleGoToStandaloneGrocery}
            >
              {intl.LINK_TEXT_CHANGE_ADDONS}
            </button>
          </div>
        </Disclosure>
      </Card>
    </div>
  );
};
